import React from 'react';
import Navigation from '../components/Navigation';
import '../App.css';

function CaseStudies() {
  return (
    <div className="App">
      <Navigation />
      
      <section className="case-studies-page">
        <div className="container">
          <h1 className="page-title">Case Studies</h1>
          <p className="page-subtitle">
            Discover how we've helped businesses reduce costs and improve efficiency through AI-powered automation
          </p>

          {/* Case Study 1: Marketing Agency */}
          <div className="case-study-card">
            <div className="case-study-header">
              <h2>AI-Powered Cost Reduction for Marketing Agency</h2>
              <p className="case-study-company">Client: CreativeFlow Marketing</p>
            </div>
            
            <div className="case-study-stats">
              <div className="case-study-stat">
                <div className="stat-number">52%</div>
                <div className="stat-label">Cost Reduction</div>
              </div>
              <div className="case-study-stat">
                <div className="stat-number">70%</div>
                <div className="stat-label">Time Saved</div>
              </div>
              <div className="case-study-stat">
                <div className="stat-number">$1.8M</div>
                <div className="stat-label">Annual Savings</div>
              </div>
            </div>

            <div className="case-study-body">
              <div className="case-study-section">
                <h3>The Challenge</h3>
                <p>
                  CreativeFlow Marketing, a mid-sized marketing agency with 50+ employees, was spending 
                  excessive time and resources on manual campaign management, client reporting, and 
                  content scheduling. Their team was overwhelmed with repetitive tasks like data collection, 
                  report generation, and social media posting. This manual work consumed 40% of their 
                  billable hours, significantly impacting profitability and preventing them from taking 
                  on more clients.
                </p>
              </div>

              <div className="case-study-section">
                <h3>Our AI Solution</h3>
                <p>
                  We implemented a comprehensive AI automation platform specifically designed for marketing 
                  agencies:
                </p>
                <ul>
                  <li><strong>AI-Powered Campaign Optimization:</strong> Machine learning algorithms that 
                  automatically adjust ad spend and targeting based on performance data in real-time</li>
                  <li><strong>Automated Client Reporting:</strong> AI system that generates personalized 
                  reports for each client, pulling data from multiple platforms and creating visualizations</li>
                  <li><strong>Intelligent Content Scheduling:</strong> AI-driven content calendar that 
                  optimizes posting times and suggests content based on audience engagement patterns</li>
                  <li><strong>Automated Data Collection:</strong> AI bots that gather metrics from Google 
                  Analytics, Facebook Ads, Instagram, and other platforms automatically</li>
                  <li><strong>Smart Email Automation:</strong> AI-powered email campaigns that personalize 
                  content based on client behavior and preferences</li>
                </ul>
              </div>

              <div className="case-study-section">
                <h3>The Results</h3>
                <p>
                  Within 4 months of implementation, CreativeFlow Marketing experienced dramatic improvements. 
                  The AI automation reduced manual work by 70%, allowing the team to focus on strategy and 
                  creative work. They eliminated 8 full-time positions dedicated to manual reporting and 
                  data entry, resulting in $1.8M in annual savings. The agency increased client capacity 
                  by 60% without hiring additional staff, and client satisfaction improved due to faster, 
                  more accurate reporting. The AI system also improved campaign performance by 35% through 
                  real-time optimization.
                </p>
              </div>
            </div>
          </div>

          {/* Case Study 2: Accounting Company */}
          <div className="case-study-card">
            <div className="case-study-header">
              <h2>AI-Driven Cost Reduction for Accounting Firm</h2>
              <p className="case-study-company">Client: Precision Accounting Group</p>
            </div>
            
            <div className="case-study-stats">
              <div className="case-study-stat">
                <div className="stat-number">48%</div>
                <div className="stat-label">Cost Reduction</div>
              </div>
              <div className="case-study-stat">
                <div className="stat-number">65%</div>
                <div className="stat-label">Time Saved</div>
              </div>
              <div className="case-study-stat">
                <div className="stat-number">$2.1M</div>
                <div className="stat-label">Annual Savings</div>
              </div>
            </div>

            <div className="case-study-body">
              <div className="case-study-section">
                <h3>The Challenge</h3>
                <p>
                  Precision Accounting Group, a regional accounting firm serving 200+ clients, was struggling 
                  with time-consuming manual processes during tax season and year-end closing. Their accountants 
                  spent countless hours on data entry, document classification, expense categorization, and 
                  financial report generation. The firm was unable to scale efficiently, turning away potential 
                  clients during peak seasons due to capacity constraints.
                </p>
              </div>

              <div className="case-study-section">
                <h3>Our AI Solution</h3>
                <p>
                  We developed a custom AI automation system tailored for accounting workflows:
                </p>
                <ul>
                  <li><strong>AI Document Processing:</strong> Intelligent OCR and machine learning that 
                  automatically extracts, categorizes, and organizes receipts, invoices, and financial documents</li>
                  <li><strong>Automated Expense Categorization:</strong> AI system that learns from historical 
                  data to automatically categorize expenses according to tax codes and client preferences</li>
                  <li><strong>Smart Data Entry:</strong> AI-powered bots that extract information from bank 
                  statements, invoices, and receipts, populating accounting software automatically</li>
                  <li><strong>Automated Financial Reporting:</strong> AI generates balance sheets, income 
                  statements, and cash flow reports with real-time data synchronization</li>
                  <li><strong>Intelligent Tax Preparation:</strong> AI system that identifies tax deductions, 
                  flags potential issues, and prepares preliminary tax returns</li>
                  <li><strong>Client Communication Automation:</strong> AI chatbot that handles routine 
                  client inquiries about tax deadlines, document requirements, and account status</li>
                </ul>
              </div>

              <div className="case-study-section">
                <h3>The Results</h3>
                <p>
                  Precision Accounting Group transformed their operations within 5 months. The AI automation 
                  reduced manual data entry by 65%, freeing accountants to focus on advisory services and 
                  complex tax planning. The firm eliminated 12 full-time positions related to data entry 
                  and document processing, saving $2.1M annually. They increased client capacity by 80% 
                  without proportional staff increases, and reduced error rates by 95%. During tax season, 
                  the firm processed 3x more returns with the same team size, and client turnaround time 
                  improved from 2 weeks to 3 days.
                </p>
              </div>
            </div>
          </div>

          {/* Case Study 3: Printing Company */}
          <div className="case-study-card">
            <div className="case-study-header">
              <h2>AI Automation for Cost Reduction in Printing Business</h2>
              <p className="case-study-company">Client: MetroPrint Solutions</p>
            </div>
            
            <div className="case-study-stats">
              <div className="case-study-stat">
                <div className="stat-number">55%</div>
                <div className="stat-label">Cost Reduction</div>
              </div>
              <div className="case-study-stat">
                <div className="stat-number">75%</div>
                <div className="stat-label">Time Saved</div>
              </div>
              <div className="case-study-stat">
                <div className="stat-number">$1.5M</div>
                <div className="stat-label">Annual Savings</div>
              </div>
            </div>

            <div className="case-study-body">
              <div className="case-study-section">
                <h3>The Challenge</h3>
                <p>
                  MetroPrint Solutions, a commercial printing company with 80 employees, faced significant 
                  operational inefficiencies in order processing, inventory management, and production 
                  scheduling. Manual order entry, quote generation, and job tracking consumed substantial 
                  time and led to errors. The company struggled with inventory optimization, often 
                  overstocking or running out of materials, and production scheduling was suboptimal, 
                  leading to delays and wasted resources.
                </p>
              </div>

              <div className="case-study-section">
                <h3>Our AI Solution</h3>
                <p>
                  We implemented an AI-powered automation system for the printing industry:
                </p>
                <ul>
                  <li><strong>AI Order Processing:</strong> Intelligent system that automatically processes 
                  incoming orders, extracts specifications, and generates quotes based on material costs, 
                  labor, and machine time</li>
                  <li><strong>Smart Inventory Management:</strong> AI algorithms that predict material needs, 
                  optimize stock levels, and automatically reorder supplies based on production schedules 
                  and historical usage patterns</li>
                  <li><strong>Automated Production Scheduling:</strong> AI system that optimizes job sequencing 
                  to minimize setup time, reduce material waste, and maximize machine utilization</li>
                  <li><strong>Quality Control Automation:</strong> AI-powered image recognition that automatically 
                  detects printing defects and quality issues before jobs are completed</li>
                  <li><strong>Predictive Maintenance:</strong> AI monitors equipment performance and predicts 
                  maintenance needs, reducing unexpected downtime</li>
                  <li><strong>Automated Customer Communication:</strong> AI system that sends order confirmations, 
                  production updates, and delivery notifications automatically</li>
                  <li><strong>Smart Pricing Engine:</strong> AI calculates optimal pricing based on material 
                  costs, complexity, urgency, and market conditions</li>
                </ul>
              </div>

              <div className="case-study-section">
                <h3>The Results</h3>
                <p>
                  MetroPrint Solutions achieved remarkable improvements within 6 months. The AI automation 
                  reduced order processing time by 75%, from 2 hours per order to 30 minutes. The company 
                  eliminated 10 full-time positions in order processing and inventory management, saving 
                  $1.5M annually. Inventory costs decreased by 40% through optimized stock levels, and 
                  production efficiency improved by 60% through better scheduling. The AI system reduced 
                  material waste by 35% and decreased machine downtime by 50% through predictive maintenance. 
                  Customer satisfaction increased significantly due to faster turnaround times and improved 
                  order accuracy.
                </p>
              </div>
            </div>
          </div>

          <div className="case-studies-cta">
            <h2>Ready to Transform Your Business?</h2>
            <p>Let's discuss how AI automation can reduce your operating costs</p>
            <a href="/#schedule" className="cta-button">Schedule a Consultation</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Vulpix Technology</h3>
              <p>Transforming businesses through intelligent automation</p>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <p>hello@vulpixtech.com</p>
              <p>+1 (555) 123-4567</p>
            </div>
            <div className="footer-section">
              <h4>Services</h4>
              <p>Process Automation</p>
              <p>Data Integration</p>
              <p>AI Solutions</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Vulpix Technology. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CaseStudies;

